import type { CveEntry } from "./normalize.js";

/**
 * Versioned IndexedDB cache for CVE pools, one record per UTC publication day.
 *
 * Contract (AC.14):
 * - A record is usable only if both versions match the current ones and it has
 *   entries. This check applies to every read path; checking only some paths
 *   would serve a stale-schema record.
 * - A record is written after every page, with the cursor for the next page.
 *   NVD often drops a transfer partway through, so persisting only at the end
 *   of a day would throw away every page that did arrive.
 * - `complete` is true only when the day's pagination is exhausted. A record
 *   that is not complete is served but keeps being refreshed; it never ends the
 *   day's fetching.
 * - Any IndexedDB failure degrades to an in-memory map plus seeds. Playback is
 *   unaffected: a private window or full quota should not silence the station.
 */

export const DB_NAME = "youarevulnerable";
export const DB_VERSION = 1;
export const STORE = "cvePools";
export const DATA_SCHEMA_VERSION = 2;
export const NORMALIZATION_VERSION = 3;

export interface CvePoolRecord {
  /** ISO date of the publication day, primary key. */
  windowStartUtc: string;
  /** The following day; the window is [start, end). */
  windowEndUtc: string;
  fetchedAt: number;
  dataSchemaVersion: number;
  normalizationVersion: number;
  /** True only after the day's pagination was exhausted. */
  complete: boolean;
  /** NVD `startIndex` of the next page to fetch for this day. */
  nextStartIndex: number;
  /** NVD's `totalResults` for this day at the last page fetched. */
  totalResults: number;
  entries: CveEntry[];
}

export type PoolSource = "cache-exact" | "cache-stale" | "seeds" | "network" | "memory";

/** What the cache can contribute to a window of days. */
export interface PoolSnapshot {
  /** Deduplicated by CVE ID, in no particular order. */
  entries: CveEntry[];
  /** Every day in the window is stored and complete; no refresh needed. */
  complete: boolean;
  /** Nothing in the window was usable, so these come from older days. */
  outOfWindow: boolean;
}

export interface PoolWriter {
  read(windowStartUtc: string): Promise<CvePoolRecord | undefined>;
  /** All records, newest window first. Needed so an incompatible newest record
   * does not hide an older usable one. */
  list(): Promise<CvePoolRecord[]>;
  newest(): Promise<CvePoolRecord | undefined>;
  write(record: CvePoolRecord): Promise<void>;
  delete(windowStartUtc: string): Promise<void>;
  close(): void;
}

/** Written by this build: versions match, so its cursor and entries can be trusted. */
export function isCurrent(record: CvePoolRecord | undefined | null): record is CvePoolRecord {
  return Boolean(
    record &&
      record.dataSchemaVersion === DATA_SCHEMA_VERSION &&
      record.normalizationVersion === NORMALIZATION_VERSION &&
      Array.isArray(record.entries),
  );
}

/** Current and has something to air. Completeness is deliberately not required. */
export function isUsable(record: CvePoolRecord | undefined | null): record is CvePoolRecord {
  return isCurrent(record) && record.entries.length > 0;
}

/**
 * `complete`, `nextStartIndex` and `totalResults` have no defaults. A default
 * of `complete: true` once let a capped fetch be cached as a finished day,
 * which stopped all further fetching until the next UTC day.
 */
export function makeRecord(input: {
  windowStartUtc: string;
  windowEndUtc: string;
  entries: CveEntry[];
  complete: boolean;
  nextStartIndex: number;
  totalResults: number;
  fetchedAt?: number;
}): CvePoolRecord {
  return {
    windowStartUtc: input.windowStartUtc,
    windowEndUtc: input.windowEndUtc,
    fetchedAt: input.fetchedAt ?? Date.now(),
    dataSchemaVersion: DATA_SCHEMA_VERSION,
    normalizationVersion: NORMALIZATION_VERSION,
    complete: input.complete,
    nextStartIndex: input.nextStartIndex,
    totalResults: input.totalResults,
    entries: input.entries,
  };
}

function request<T>(req: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error ?? new Error("IndexedDB request failed"));
  });
}

export class IndexedDbPoolWriter implements PoolWriter {
  private database: IDBDatabase | null = null;
  private failed = false;

  constructor(
    private readonly factory: IDBFactory | undefined = globalThis.indexedDB,
    private readonly dbName: string = DB_NAME,
  ) {}

  private async open(): Promise<IDBDatabase> {
    if (this.database) return this.database;
    if (this.failed || !this.factory) throw new Error("IndexedDB unavailable");
    const database = await new Promise<IDBDatabase>((resolve, reject) => {
      const openRequest = this.factory!.open(this.dbName, DB_VERSION);
      openRequest.onupgradeneeded = () => {
        const db = openRequest.result;
        if (!db.objectStoreNames.contains(STORE)) {
          db.createObjectStore(STORE, { keyPath: "windowStartUtc" });
        }
      };
      openRequest.onsuccess = () => resolve(openRequest.result);
      openRequest.onerror = () => reject(openRequest.error ?? new Error("open failed"));
      openRequest.onblocked = () => reject(new Error("open blocked"));
    });
    this.database = database;
    return database;
  }

  async read(windowStartUtc: string): Promise<CvePoolRecord | undefined> {
    const database = await this.open();
    const tx = database.transaction(STORE, "readonly");
    return request<CvePoolRecord | undefined>(tx.objectStore(STORE).get(windowStartUtc));
  }

  async newest(): Promise<CvePoolRecord | undefined> {
    const all = await this.list();
    return all[0];
  }

  async list(): Promise<CvePoolRecord[]> {
    const database = await this.open();
    const tx = database.transaction(STORE, "readonly");
    const records = await request<CvePoolRecord[]>(tx.objectStore(STORE).getAll());
    // Keys are ISO dates, so descending key order is descending recency.
    return records.sort((a, b) => (a.windowStartUtc < b.windowStartUtc ? 1 : -1));
  }

  async write(record: CvePoolRecord): Promise<void> {
    const database = await this.open();
    const tx = database.transaction(STORE, "readwrite");
    await request(tx.objectStore(STORE).put(record));
  }

  async delete(windowStartUtc: string): Promise<void> {
    const database = await this.open();
    const tx = database.transaction(STORE, "readwrite");
    await request(tx.objectStore(STORE).delete(windowStartUtc));
  }

  close(): void {
    this.database?.close();
    this.database = null;
  }
}

/** Fallback writer for private mode, quota failures, or a missing implementation. */
export class MemoryPoolWriter implements PoolWriter {
  private readonly records = new Map<string, CvePoolRecord>();

  read(windowStartUtc: string): Promise<CvePoolRecord | undefined> {
    return Promise.resolve(this.records.get(windowStartUtc));
  }

  newest(): Promise<CvePoolRecord | undefined> {
    return this.list().then((records) => records[0]);
  }

  list(): Promise<CvePoolRecord[]> {
    const records = [...this.records.values()].sort((a, b) =>
      a.windowStartUtc < b.windowStartUtc ? 1 : -1,
    );
    return Promise.resolve(records);
  }

  write(record: CvePoolRecord): Promise<void> {
    this.records.set(record.windowStartUtc, record);
    return Promise.resolve();
  }

  delete(windowStartUtc: string): Promise<void> {
    this.records.delete(windowStartUtc);
    return Promise.resolve();
  }

  close(): void {
    this.records.clear();
  }
}

export class ResilientPoolWriter implements PoolWriter {
  private readonly fallback = new MemoryPoolWriter();
  private warned = false;

  constructor(private readonly primary: PoolWriter) {}

  private warn(error: unknown): void {
    if (this.warned) return;
    this.warned = true;
    console.warn("CVE cache unavailable; using in-memory pool for this session.", error);
  }

  async read(windowStartUtc: string): Promise<CvePoolRecord | undefined> {
    try {
      return await this.primary.read(windowStartUtc);
    } catch (error) {
      this.warn(error);
      return this.fallback.read(windowStartUtc);
    }
  }

  async newest(): Promise<CvePoolRecord | undefined> {
    try {
      return await this.primary.newest();
    } catch (error) {
      this.warn(error);
      return this.fallback.newest();
    }
  }

  async list(): Promise<CvePoolRecord[]> {
    try {
      return await this.primary.list();
    } catch (error) {
      this.warn(error);
      return this.fallback.list();
    }
  }

  async write(record: CvePoolRecord): Promise<void> {
    try {
      await this.primary.write(record);
    } catch (error) {
      this.warn(error);
      await this.fallback.write(record);
    }
  }

  async delete(windowStartUtc: string): Promise<void> {
    try {
      await this.primary.delete(windowStartUtc);
    } catch (error) {
      this.warn(error);
      await this.fallback.delete(windowStartUtc);
    }
  }

  close(): void {
    try {
      this.primary.close();
    } catch {
      // ignore
    }
  }
}

/**
 * Read policy, in one place, for a window given as its day keys:
 *   usable days in the window -> serve them; complete only if every day is
 *   stored and complete
 *   else usable days from before the window -> serve them, refresh
 *   else nothing (caller falls back to seeds, then refresh)
 */
export async function readPool(writer: PoolWriter, days: string[]): Promise<PoolSnapshot | null> {
  const records = (await writer.list().catch(() => [])).filter((record) => isCurrent(record));
  const wanted = new Set(days);
  const inWindow = records.filter((record) => wanted.has(record.windowStartUtc));
  const complete = days.every((day) =>
    inWindow.some((record) => record.windowStartUtc === day && record.complete),
  );

  const fromWindow = mergeEntries(inWindow);
  if (fromWindow.length > 0) return { entries: fromWindow, complete, outOfWindow: false };

  const older = mergeEntries(records.filter((record) => !wanted.has(record.windowStartUtc)));
  if (older.length > 0) return { entries: older, complete: false, outOfWindow: true };

  return null;
}

/** One entry per CVE ID; a later record wins. */
export function mergeEntries(records: Array<Pick<CvePoolRecord, "entries">>): CveEntry[] {
  const byId = new Map<string, CveEntry>();
  for (const record of records) {
    for (const entry of record.entries) byId.set(entry.id, entry);
  }
  return [...byId.values()];
}
